import React, { useMemo, useCallback } from "react";
import { Box, Grid, Button, Typography } from "@mui/material";
import { BooleanInput, NumberInput, useNotify } from "react-admin";
import { useFormContext, useWatch, useFormState } from "react-hook-form";
import { useEffect, useRef, useState } from "react";

const noWheel = { onWheel: (e) => e.target.blur() };

const DEFAULT_SIZES = ["XS", "S", "M", "L", "XL", "2XL", "3XL"];
const DEFAULT_WEIGHT_TEMPLATE = { XS:1, S:3, M:6, L:6, XL:3, "2XL":1, "3XL":0 };

const blockInvalidKeys = (e) => {
  if (['e', 'E', '+', '-', '.'].includes(e.key)) {
    e.preventDefault();
  }
};
const nonNegativeInteger = (v) => {
  if (v === undefined || v === null || v === '') return undefined;
  const n = Number(v);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return '請輸入整數';
  if (n < 0) return '不可為負數';
  return undefined;
};

/**
 * 規格權重元件
 * props:
 * - fieldBase: string   例："samplingSpecs"（實際儲存路徑為 `${fieldBase}.sizes`）
 * - title?: string      標題
 * - sizesList?: string[]          （預設 DEFAULT_SIZES）
 * - autoTemplate?: Record<string, number> （預設 DEFAULT_WEIGHT_TEMPLATE）
 */
const SpecWeights = ({
  fieldBase,
  title = "規格選項與分配權重",
  sizesList = DEFAULT_SIZES,
  autoTemplate = DEFAULT_WEIGHT_TEMPLATE,
}) => {
  const { setValue, getValues, control } = useFormContext();
  const sizesPath = `${fieldBase}.sizes`;
  const sizes = useWatch({ control, name: sizesPath });
  const prevEnabledRef = React.useRef([]);

  useEffect(() => {
    const arr = Array.isArray(sizes) ? sizes : [];
    const toClear = [];

    arr.forEach((row, i) => {
      const now = !!row?.enabled;
      const prev = prevEnabledRef.current[i];

      // 關閉，或初始就是關閉且 weight 仍有值，都清空
      const turnedOff = prev === true && now === false;
      const initiallyOffWithValue = prev === undefined && now === false && row?.weight != null && row?.weight !== "";

      if (turnedOff || initiallyOffWithValue) {
        toClear.push(i);
      }
      prevEnabledRef.current[i] = now;
    });

    if (toClear.length) {
      toClear.forEach((idx) => {
        setValue(`${sizesPath}.${idx}.weight`, undefined, { shouldDirty: true, shouldValidate: true });
      });
    }
  }, [sizes, setValue, sizesPath]);

  // 確保每個預設 size 都有一筆
  const normalized = useMemo(() => {
    const map = new Map((Array.isArray(sizes) ? sizes : []).map((s) => [s.key, s]));
    return sizesList.map((k) => map.get(k) || { key: k, enabled: true, weight: undefined });
  }, [sizes, sizesList]);

  // 需要時才初始化，避免每次 render 都 setValue
  const ensureInited = useCallback(() => {
    const cur = getValues(sizesPath);
    const need =
      !Array.isArray(cur) ||
      cur.length !== normalized.length ||
      normalized.some((r, i) => (cur && cur[i] ? cur[i].key !== r.key : true));
    if (need) setValue(sizesPath, normalized, { shouldDirty: false });
  }, [getValues, setValue, normalized, sizesPath]);
  ensureInited();

  // 顯示用：勾選的權重總和
  const totalWeight = useMemo(
    () =>
      normalized.reduce((sum, r) => {
        if (!r.enabled) return sum;
        const n = Number(r.weight);
        return sum + (isFinite(n) ? n : 0);
      }, 0),
    [normalized]
  );

  // 自動分配權重
  const handleAuto = () => {
    const next = normalized.map((r) => {
      if (!r.enabled) return { ...r, weight: undefined };
      const tplVal = autoTemplate[r.key] != null ? autoTemplate[r.key] : 1;
      return { ...r, weight: tplVal };
    });
    setValue(sizesPath, next, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
        <Typography variant="h6">{title}</Typography>
        <Button variant="contained" onClick={handleAuto}>自動分配權重</Button>
        <Box sx={{ ml: "auto", color: "text.secondary" }}>權重總和 = {totalWeight || 0}</Box>
      </Box>

      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        勾選要顯示在前台的規格並輸入「權重」。可按<strong>自動分配權重</strong>快速填入後再調整。
      </Typography>

      <Grid container spacing={2}>
        {normalized.map((row, idx) => (
          <Grid key={`${fieldBase}-${row.key}`} item xs={12} sm={6} md={4}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                alignItems: "center",
                gap: 1,
                p: 1.5,
                borderRadius: 2,
                border: (t) => `1px dashed ${t.palette.divider}`,
              }}
            >
              <BooleanInput label={row.key} source={`${sizesPath}.${idx}.enabled`} />
              <NumberInput
                source={`${sizesPath}.${idx}.weight`}
                label="權重"
                disabled={!row.enabled}
                fullWidth
                {...noWheel}
                validate={[nonNegativeInteger]}
                inputProps={{
                  min: 0,
                  step: 1,
                  pattern: '[0-9]*',
                }}
                onKeyDown={blockInvalidKeys}
                parse={(v) => (v === '' || v == null ? undefined : Math.max(0, Math.trunc(Number(v))))}
              />
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default SpecWeights;
